import cn from 'classnames'
import { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ReactComponent as ErrorIcon } from '../../assets/img/error.svg'
import { ReactComponent as InfoIcon } from '../../assets/img/info.svg'
import { ReactComponent as SuccessIcon } from '../../assets/img/success.svg'
import { ReactComponent as WarningIcon } from '../../assets/img/warning.svg'
import { useRemoveToast } from '../../model/redux-toast.actions'
import { DURATION } from '../../model/redux-toast.config'
import { type Toast as IToast, ToastType } from '../../types'

import styles from './toast.module.css'

type ToastProps = {
  className?: string
  toast: IToast
  side: 'right' | 'left'
}

export const Toast = ({ className, toast, side }: ToastProps) => {
  const { t } = useTranslation('toast')
  const { remove } = useRemoveToast()
  const duration = toast.duration || DURATION
  const [isExiting, setIsExiting] = useState(false)

  useEffect(() => {
    // Анимация исчезновения начинается немедленно
    const exitTimer = setTimeout(() => setIsExiting(true), duration - 1000)
    // После анимации исчезновения удаляем toast
    const removeTimer = setTimeout(() => remove(toast.id), duration)

    return () => {
      clearTimeout(exitTimer)
      clearTimeout(removeTimer)
    }
  }, [remove, toast.id, duration])

  const iconsMapper: Record<ToastType, ReactNode> = {
    error: <ErrorIcon className={styles.icon} />,
    warning: <WarningIcon className={styles.icon} />,
    info: <InfoIcon className={styles.icon} />,
    success: <SuccessIcon className={styles.icon} />
  }
  const titleMapper: Record<ToastType, string> = {
    error: t('error'),
    warning: t('warning'),
    info: t('info'),
    success: t('success')
  }

  return (
    <div
      key={toast.id}
      onClick={() => remove(toast.id)}
      className={cn(styles.toast, className, {
        [styles.warning]: toast.type === 'warning',
        [styles.info]: toast.type === 'info',
        [styles.error]: toast.type === 'error',
        [styles.success]: toast.type === 'success',
        [styles.exitRight]: isExiting === true && side === 'right',
        [styles.enterRight]: isExiting === false && side === 'right',
        [styles.exitLeft]: isExiting === true && side === 'left',
        [styles.enterLeft]: isExiting === false && side === 'left'
      })}
    >
      {iconsMapper[toast.type]}
      <span className={styles.title}>{titleMapper[toast.type]}</span>
      <span className={styles.text}>{toast.message}</span>
    </div>
  )
}
